import React from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles';
import VirusBoard from './VirusBoard';
import random from '../../core/random';
import RootUnit from './Unit';
import s from './VirusBoard.css';

const MIN_N_COUNTRIES = 6;
const MAX_N_COUNTRIES = 8;
const MIN_CITIES_PER_COUNTRY = 3;
const MAX_CITIES_PER_COUNTRY = 5;
const MIN_POP = 100000000;
const MAX_POP = 1000000000;
const MIN_INCU = 1;
const MAX_INCU = 14;
const MIN_INCU_TO_DEAD = 0;
const MAX_INCU_TO_DEAD = 7;
const MIN_DEAD_TO_RECO = 1;
const MAX_DEAD_TO_RECO = 7;
const MIN_DR_INV = 1;
const MAX_DR_INV = 100;
const MIN_DAILY_GROWTH = 1.2;
const MAX_DAILY_GROWTH = 1.3;
const POP_ECCENTRICITY = 3.5;
const MIN_HOSPITAL = 1E-3;
const MAX_HOSPITAL = 5E-3;

const countryConfig = {
  thresh: 0.5,
  scale: 120,
  shiftRatio: 0.18,
  gaussMinRatio: 0.008,
  gaussMaxRatio: 0.1,
  zScore: 2,
  minNGauss: 5,
  maxNGauss: 15,
  padding: 0.08,
};
const cityConfig = {
  thresh: 0.0,
  scale: 240,
  shiftRatio: 0.18,
  gaussMinRatio: 0.02,
  gaussMaxRatio: 0.08,
  zScore: 2,
  minNGauss: 6,
  maxNGauss: 8,
  padding: 0,
};

const emptyChars = new Set([' ', '-']);
const capitalize = (str) => {
  let capitalizeNext = true;
  let result = '';
  for (let i = 0; i < str.length; i++) {
    const char = str.charAt(i);
    if (capitalizeNext) {
      result += char.toUpperCase();
    } else {
      result += char;
    }

    if (emptyChars.has(char)) {
      capitalizeNext = true;
    } else {
      capitalizeNext = false;
    }
  }
  return result;
};

class Virus extends React.Component {
  static propTypes = {
    seed: PropTypes.number,
  }

  constructor(props) {
    super(props);
    const rng = new random.Rng(this.props.seed);
    this.state = { rng };
  }

  componentDidMount() {
    setImmediate(() => {
      const data = this.generateData(this.state.rng);
      this.setState(data);
    });
    this.requestNames();
  }

  requestNames() {
    fetch(`/data/who/names?seed=${random.seedToHex(this.props.seed)}`)
      .then((response) => response.json())
      .then((data) => {
        this.setState({
          countryNames: data.countryNames.map(capitalize),
          cityNames: data.cityNames.map(capitalize),
        });
      });
  }

  generateData(rng) {
    const globalUnit = new RootUnit('unit', 2 / 3);
    globalUnit.meta.name = 'Planet';

    const nCountries = rng.int(MIN_N_COUNTRIES, MAX_N_COUNTRIES + 1);
    globalUnit.generateChildren(nCountries, rng, countryConfig);
    for (let i = 0; i < nCountries; i++) {
      const country = globalUnit.children[i];
      const nCities = rng.int(
        MIN_CITIES_PER_COUNTRY,
        MAX_CITIES_PER_COUNTRY,
      );
      country.generateChildren(nCities, rng, cityConfig);
    }

    const incuDays = rng.int(MIN_INCU, MAX_INCU + 1);
    const deadDays = incuDays + rng.int(MIN_INCU_TO_DEAD, MAX_INCU_TO_DEAD + 1);
    const recoDays = deadDays + rng.int(MIN_DEAD_TO_RECO, MAX_DEAD_TO_RECO + 1);
    const dr = 1 / rng.float(MIN_DR_INV, MAX_DR_INV);
    const virusData = {
      incuDays,
      deadDays,
      recoDays,
      deathRate: dr,
      dailyGrowth: rng.float(MIN_DAILY_GROWTH, MAX_DAILY_GROWTH),
    };
    const invGrowth = 1 / virusData.dailyGrowth;
    // total_num_infected / num_infected_yesterday
    const infectedRatio = (1 - invGrowth ** virusData.recoDays) / (1 - invGrowth);
    virusData.infect = virusData.dailyGrowth / infectedRatio;
    const highRatio = 3;
    virusData.highDeathRate = Math.min(
      highRatio * dr,
      1 - (1 - dr) / 3,
    );
    // virusData.seasonality = rng.float(0, (virusData.dailyGrowth - 1) / 2);

    const pop = rng.int(MIN_POP, MAX_POP);
    globalUnit.incr(
      'pop',
      pop,
      rng,
      { eccentricity: POP_ECCENTRICITY },
    );
    globalUnit.traverse((unit) => {
      const { meta, shapes } = unit;
      meta.density = meta.pop / meta.area;
      shapes.forEach((shape) => {
        delete shape.mask;
      });
    });

    globalUnit.children.forEach((country) => {
      country.meta.wealth = rng.float(0, 1);
      country.meta.hospital = rng.float(MIN_HOSPITAL, MAX_HOSPITAL);
      country.meta.isCountry = true;
      country.meta.will = 0;
      country.children.forEach((city) => {
        city.meta.wealth = country.meta.wealth;
      });
    });

    return {
      unitTree: globalUnit,
      virusData,
    };
  }

  render() {
    return (
      <VirusBoard
        countryNames={this.state.countryNames}
        cityNames={this.state.cityNames}
        unitTree={this.state.unitTree}
        virusData={this.state.virusData}
        rng={this.state.rng}
        seed={this.props.seed}
      />
    );
  }
}

export default withStyles(s)(Virus);



// WEBPACK FOOTER //
// src/components/Virus/Virus.js